import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface SubsidieIntakeData {
  // Bedrijfsgegevens
  bedrijfsgrootte?: 'klein' | 'middel' | 'mkb' | 'groot';
  fte?: number;
  sector?: string;
  postcode_2cijferig?: string;

  // Locatie
  locatie_publiek_toegankelijk?: boolean;
  locatie_type?: 'bedrijventerrein' | 'nabij_weg' | 'overig';
  afstand_an_weg_km?: number;
  bereikbaar_6m_verhard?: boolean;
  toegankelijk_40u_week?: boolean;

  // Netaansluiting
  netaansluiting_kva?: number;
  grootverbruiker?: boolean;
  netcongestie_afname?: boolean;
  netbeheerder_bevestiging_2jaar?: boolean;

  // Laadinfra
  laadstations?: Array<{
    type: 'AC' | 'DC';
    vermogen_kw: number;
    aantal: number;
    connector?: string;
  }>;
  totaal_laadvermogen_kw?: number;
  doelvoertuigen?: string[];

  // Batterij
  batterij_gepland?: boolean;
  batterij_kwh?: number;
  batterij_kw?: number;
  batterij_70pct_laadinfra?: boolean;

  // EMS / PV
  ems_gepland?: boolean;
  pv_kwp?: number;

  // Trucks
  trucks_gepland?: Array<{
    categorie: string;
    type: string;
    brandstof: string;
    verkoopprijs?: number;
  }>;

  // Investering
  investering_totaal?: number;
  investering_laadinfra?: number;
  investering_batterij?: number;
  investering_ems?: number;
}

/**
 * Waar een subsidie-intake aan hangt.
 *
 * T1.3 — dit was `entityType: string` naast `entityId: string`, met de database
 * die de soort alleen tegen een CHECK-lijst hield. Wie een waarde schreef die
 * niet in die lijst stond kreeg geen foutmelding maar een mislukte insert, en
 * dat is precies wat de Subsidie-tab (`'laadplein_scenario'`) en de
 * subsidiepagina (`'laadplein_locatie'`) maandenlang deden. Nu is elke variant
 * een eigen vorm: een verkeerde waarde compileert niet meer.
 *
 * `simulatie` is het zelfstandige geval — geen eigenaar, wel een eigen naam.
 * Let op: daar is `id` de id van de intake-rij zélf, niet die van een eigenaar.
 */
export type IntakeTarget =
  | { kind: 'project'; id: string }
  | { kind: 'charging_location'; id: string }
  | { kind: 'charging_opportunity'; id: string }
  | { kind: 'scenario'; id: string }
  | { kind: 'simulatie'; id: string };

/** FK-kolom per gekoppelde vorm. `simulatie` ontbreekt hier bewust. */
const TARGET_COLUMN = {
  project: 'project_id',
  charging_location: 'charging_location_id',
  charging_opportunity: 'charging_opportunity_id',
  scenario: 'scenario_id',
} as const;

/**
 * De kolom waarop je de intake voor dit doel terugvindt.
 *
 * Het retourtype is bewust een letterlijke unie en geen `string`: met `string`
 * kan de PostgREST-typing de kolom niet meer thuisbrengen en loopt de
 * inferentie over het gegenereerde Database-type vast (TS2589).
 */
export type IntakeLookupColumn =
  | 'id'
  | 'project_id'
  | 'charging_location_id'
  | 'charging_opportunity_id'
  | 'scenario_id';

export function intakeLookupColumn(target: IntakeTarget): IntakeLookupColumn {
  return target.kind === 'simulatie' ? 'id' : TARGET_COLUMN[target.kind];
}

export interface SubsidieIntake {
  id: string;
  project_id: string | null;
  charging_location_id: string | null;
  charging_opportunity_id: string | null;
  scenario_id: string | null;
  is_simulatie: boolean;
  naam: string | null;
  data: SubsidieIntakeData;
  ingevuld_door: string | null;
  volledigheid_pct: number;
  created_at: string;
  updated_at: string;
  /** LEGACY — leeg voor nieuwe rijen, verdwijnt met migratie 029. */
  entity_type?: string | null;
  entity_id?: string | null;
}

// Stabiele lege-object-referentie: voorkomt dat consumenten (read-through-panels)
// hun useMemo's elke render herberekenen zolang er nog geen intake-rij bestaat.
const EMPTY_INTAKE: SubsidieIntakeData = {};

function calculateCompleteness(data: SubsidieIntakeData): number {
  const fields = [
    data.bedrijfsgrootte,
    data.locatie_publiek_toegankelijk !== undefined,
    data.locatie_type,
    data.netaansluiting_kva,
    data.grootverbruiker !== undefined,
    data.netcongestie_afname !== undefined,
    data.laadstations && data.laadstations.length > 0,
    data.totaal_laadvermogen_kw,
    data.doelvoertuigen && data.doelvoertuigen.length > 0,
    data.batterij_gepland !== undefined,
    data.investering_totaal,
  ];
  const filled = fields.filter(Boolean).length;
  return Math.round((filled / fields.length) * 100);
}

export function useSubsidieIntake(target: IntakeTarget) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const kolom = intakeLookupColumn(target);

  const intakeQuery = useQuery({
    queryKey: ['sub_intake', target.kind, target.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sub_intakes')
        .select('*')
        .eq(kolom, target.id)
        .maybeSingle();
      if (error) throw error;
      return data as SubsidieIntake | null;
    },
    enabled: !!target.id,
  });

  const upsertMutation = useMutation({
    mutationFn: async (intakeData: SubsidieIntakeData) => {
      const velden = {
        data: intakeData as any,
        ingevuld_door: user?.id ?? null,
        volledigheid_pct: calculateCompleteness(intakeData),
        updated_at: new Date().toISOString(),
      };

      // Geen .upsert() meer. De oude versie leunde op
      // `onConflict: 'entity_type,entity_id'`; die composiet bestaat niet meer.
      // De vervangers zijn *partiële* unique indexen (`where <kolom> is not
      // null`), en Postgres kan een partiële index alleen als conflict-target
      // gebruiken als de query zijn WHERE-predicaat herhaalt — iets wat
      // PostgREST's on_conflict niet meestuurt. Dus expliciet zoeken en dan
      // schrijven.
      const { data: bestaand, error: zoekFout } = await supabase
        .from('sub_intakes')
        .select('id')
        .eq(kolom, target.id)
        .maybeSingle();
      if (zoekFout) throw zoekFout;

      if (bestaand) {
        const { data, error } = await supabase
          .from('sub_intakes')
          .update(velden)
          .eq('id', bestaand.id)
          .select()
          .single();
        if (error) throw error;
        return data as SubsidieIntake;
      }

      if (target.kind === 'simulatie') {
        // Een simulatie krijgt zijn naam bij het aanmaken (useSubsidieSimulaties)
        // en de vorm-CHECK eist die naam. Hier een rij verzinnen kan dus niet.
        throw new Error('Simulatie bestaat niet meer; maak hem opnieuw aan.');
      }

      // Expliciete kolomnaam in plaats van een computed key: met een computed
      // key loopt de inferentie over het gegenereerde Database-type vast
      // (TS2589), en zo lees je bovendien meteen welke vier vormen er zijn.
      const eigenaar =
        target.kind === 'project' ? { project_id: target.id }
        : target.kind === 'charging_location' ? { charging_location_id: target.id }
        : target.kind === 'charging_opportunity' ? { charging_opportunity_id: target.id }
        : { scenario_id: target.id };

      const { data, error } = await supabase
        .from('sub_intakes')
        .insert({ ...velden, ...eigenaar, is_simulatie: false })
        .select()
        .single();
      if (error) throw error;
      return data as SubsidieIntake;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub_intake', target.kind, target.id] });
      queryClient.invalidateQueries({ queryKey: ['sub_matches', target.kind, target.id] });
      // Read-through-caches óók verversen, en bewust op de hele prefix. Een
      // schrijfactie op één niveau verandert wat een ander niveau oplost: sla je
      // hier voor het eerst iets op het Ontwerp op, dan wint die rij voortaan
      // van de project-intake — en zonder deze regel blijft het paneel
      // "Overgenomen van project X" tonen terwijl dat niet meer klopt. Andersom
      // net zo: een intake op een Project raakt élk Ontwerp eronder dat er zelf
      // geen heeft. Welke scenario's dat zijn weten we hier niet, dus alles.
      queryClient.invalidateQueries({ queryKey: ['effective-intake'] });
      toast.success('Subsidie-intake opgeslagen');
    },
    onError: (error) => {
      toast.error('Fout bij opslaan intake: ' + error.message);
    },
  });

  return {
    intake: intakeQuery.data,
    intakeData: (intakeQuery.data?.data ?? EMPTY_INTAKE) as SubsidieIntakeData,
    isLoading: intakeQuery.isLoading,
    volledigheid: intakeQuery.data?.volledigheid_pct ?? 0,
    saveIntake: upsertMutation.mutateAsync,
    isSaving: upsertMutation.isPending,
  };
}

// --- Standalone simulaties beheren ---

export function useSubsidieSimulaties() {
  const queryClient = useQueryClient();

  const simulatiesQuery = useQuery({
    queryKey: ['sub_simulaties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sub_intakes')
        .select('*')
        .eq('is_simulatie', true)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return data as SubsidieIntake[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (naam: string) => {
      const { data, error } = await supabase
        .from('sub_intakes')
        .insert({
          // Het zelfstandige geval: geen enkele FK, wel een naam. De
          // kunstmatige entity_id die hier stond is niet meer nodig.
          is_simulatie: true,
          naam,
          data: {},
          volledigheid_pct: 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data as SubsidieIntake;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub_simulaties'] });
      toast.success('Simulatie aangemaakt');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('sub_intakes').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub_simulaties'] });
      toast.success('Simulatie verwijderd');
    },
  });

  return {
    simulaties: simulatiesQuery.data ?? [],
    isLoading: simulatiesQuery.isLoading,
    createSimulatie: createMutation.mutateAsync,
    deleteSimulatie: deleteMutation.mutateAsync,
  };
}
